import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    HardDrive, Film, FileText, Crosshair, FileSearch, Image, Search,
    Play, Share2, Trash2, Pencil, Download, MoreVertical,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, fileUrl } from "@/lib/api";
import { formatLocalDateTime } from "@/lib/utils";

const ICONS = { clips: Film, reports: FileText, matches: Crosshair, exports: FileSearch, screenshots: Image };

export default function Storage() {
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [active, setActive] = useState(null);       // clip open in player modal
    const [playError, setPlayError] = useState(null);
    const [renaming, setRenaming] = useState(null);   // clip being renamed
    const [renameVal, setRenameVal] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/storage").then(({ data }) => setData(data));
    useEffect(() => { api.get("/storage").then(({ data }) => setData(data)); }, []);

    if (!data) return <Loader label="Loading storage center" />;

    const shown = (data.clips || []).filter((c) => (c.title || "").toLowerCase().includes(q.toLowerCase()));

    const openPlayer = (c) => { setPlayError(null); setActive(c); };

    const share = async (id) => {
        try {
            const { data: r } = await api.post(`/clips/${id}/share`);
            const url = `${window.location.origin}${r.path}`;
            try { await navigator.clipboard.writeText(url); toast.success("Public share link copied to clipboard!"); }
            catch { toast.success(`Share link: ${url}`); }
        } catch { toast.error("Could not create share link."); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this clip? This cannot be undone.")) return;
        try {
            await api.delete(`/clips/${id}`);
            toast("Clip deleted.");
            if (active?.id === id) setActive(null);
            load();
        } catch { toast.error("Could not delete clip."); }
    };

    const startRename = (c) => { setRenameVal(c.title || ""); setRenaming(c); };
    const saveRename = async () => {
        const title = renameVal.trim();
        if (!title) { toast.error("Name cannot be empty."); return; }
        setSaving(true);
        try {
            await api.patch(`/clips/${renaming.id}`, { title });
            toast.success("Clip renamed.");
            setData((d) => ({ ...d, clips: d.clips.map((c) => (c.id === renaming.id ? { ...c, title } : c)) }));
            if (active?.id === renaming.id) setActive((a) => ({ ...a, title }));
            setRenaming(null);
        } catch { toast.error("Could not rename clip."); }
        finally { setSaving(false); }
    };

    return (
        <div className="ww-fade-up">
            <PageHeader title="Storage Center" subtitle="All your matches, clips, reports and exports — searchable in one place." />

            <div className="bg-surface border border-border rounded-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold flex items-center gap-2"><HardDrive className="w-4 h-4 text-volt" /> Storage Usage</span>
                    <span className="font-mono text-white">{data.used_gb} / {data.limit_gb} GB</span>
                </div>
                <Progress value={data.percent} className="h-2 bg-obsidian" />
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5">
                    {Object.entries(data.counts).map(([k, v]) => {
                        const Icon = ICONS[k] || FileText;
                        return (
                            <div key={k} className="bg-obsidian border border-border rounded-sm p-3 text-center" data-testid={`storage-${k}`}>
                                <Icon className="w-4 h-4 text-volt mx-auto mb-1.5" />
                                <div className="font-mono text-lg text-white">{v}</div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground capitalize">{k}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="bg-surface border border-border rounded-sm p-6">
                <div className="flex items-center justify-between mb-4 gap-3">
                    <h3 className="font-heading font-bold">Stored Clips</h3>
                    <div className="relative w-full max-w-xs">
                        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input data-testid="storage-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clips..."
                            className="bg-obsidian border-border rounded-sm h-9 pl-9" />
                    </div>
                </div>
                <div className="divide-y divide-border/60">
                    {shown.map((c) => (
                        <div key={c.id} className="flex items-center justify-between py-2.5 gap-3" data-testid={`storage-clip-${c.id}`}>
                            <button onClick={() => openPlayer(c)} data-testid={`storage-open-${c.id}`}
                                className="flex items-center gap-3 min-w-0 flex-1 text-left group">
                                <span className="relative w-14 h-9 rounded-sm overflow-hidden bg-obsidian border border-border shrink-0 grid place-items-center">
                                    {c.thumbnail_path || c.thumbnail_url
                                        ? <img src={c.thumbnail_path ? fileUrl(c.thumbnail_media_url) : c.thumbnail_url} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                        : <Film className="w-4 h-4 text-muted-foreground" />}
                                    <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                                        <Play className="w-4 h-4 text-volt" />
                                    </span>
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm text-white truncate group-hover:text-volt transition-colors">{c.title}</span>
                                    <span className="block text-[11px] text-muted-foreground font-mono">{c.game} · {c.event_type} · {c.duration_sec}s</span>
                                    {c.created_at && <span className="block text-[10px] text-muted-foreground/70 font-mono" data-testid={`storage-date-${c.id}`}>{formatLocalDateTime(c.created_at)}</span>}
                                </span>
                            </button>
                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => openPlayer(c)} data-testid={`storage-open-btn-${c.id}`} title="Open video"
                                    className="hidden sm:grid place-items-center w-8 h-8 rounded-sm text-muted-foreground hover:text-volt hover:bg-white/5">
                                    <Play className="w-4 h-4" />
                                </button>
                                <button onClick={() => startRename(c)} data-testid={`storage-rename-btn-${c.id}`} title="Edit name"
                                    className="hidden sm:grid place-items-center w-8 h-8 rounded-sm text-muted-foreground hover:text-white hover:bg-white/5">
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => share(c.id)} data-testid={`storage-share-btn-${c.id}`} title="Share"
                                    className="hidden sm:grid place-items-center w-8 h-8 rounded-sm text-muted-foreground hover:text-volt hover:bg-white/5">
                                    <Share2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => remove(c.id)} data-testid={`storage-delete-btn-${c.id}`} title="Delete"
                                    className="hidden sm:grid place-items-center w-8 h-8 rounded-sm text-muted-foreground hover:text-alert hover:bg-white/5">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                                {/* Mobile: collapse into a menu */}
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="sm:hidden grid place-items-center w-8 h-8 rounded-sm text-muted-foreground hover:text-white hover:bg-white/5" data-testid={`storage-menu-${c.id}`}>
                                            <MoreVertical className="w-4 h-4" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="bg-surface border-border">
                                        <DropdownMenuItem onClick={() => openPlayer(c)}><Play className="w-4 h-4 mr-2" /> Open video</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => startRename(c)}><Pencil className="w-4 h-4 mr-2" /> Edit name</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => share(c.id)}><Share2 className="w-4 h-4 mr-2" /> Share</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => remove(c.id)} className="text-alert focus:text-alert"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                    ))}
                    {shown.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">No clips match your search.</div>}
                </div>
            </div>

            {/* Player modal */}
            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="bg-surface border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{active?.title}</DialogTitle>
                        <DialogDescription className="font-mono text-xs">{active?.event_type} · {active?.game} · {active?.duration_sec}s{active?.created_at ? ` · ${formatLocalDateTime(active.created_at)}` : ""}</DialogDescription>
                    </DialogHeader>
                    <div className="aspect-video bg-obsidian border border-border rounded-sm overflow-hidden">
                        {active?.video_url && playError !== active.id
                            ? <video key={active.id} src={fileUrl(active.video_media_url)} controls autoPlay playsInline
                                className="w-full h-full" data-testid="storage-clip-player"
                                onError={() => setPlayError(active.id)} />
                            : active?.video_url && playError === active.id
                                ? <div className="relative w-full h-full grid place-items-center text-center px-4" data-testid="storage-play-error">
                                    <div>
                                        <p className="text-sm text-white font-semibold mb-1">Can’t play this clip here</p>
                                        <p className="text-xs text-muted-foreground mb-3">Your browser may not support this video codec.</p>
                                        <a href={fileUrl(active.video_media_url)} download className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-volt text-black font-bold text-sm">
                                            <Download className="w-4 h-4" /> Download clip
                                        </a>
                                    </div>
                                </div>
                                : (active?.thumbnail_path || active?.thumbnail_url)
                                    ? <div className="relative w-full h-full">
                                        <img src={active?.thumbnail_path ? fileUrl(active.thumbnail_media_url) : active?.thumbnail_url} alt={active?.title} className="w-full h-full object-cover" />
                                        <div className="absolute inset-x-0 bottom-0 bg-black/75 px-3 py-2 text-[11px] text-warn font-mono flex items-center gap-1.5" data-testid="storage-image-only">
                                            <Film className="w-3.5 h-3.5 shrink-0" /> Image-only highlight — no video recorded.
                                        </div>
                                    </div>
                                    : <div className="w-full h-full grid place-items-center"><p className="text-xs text-muted-foreground font-mono">No media available.</p></div>}
                    </div>
                    {active && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            <button onClick={() => startRename(active)} data-testid="storage-dialog-rename"
                                className="h-10 rounded-sm border border-border text-white hover:bg-white/5 font-semibold flex items-center justify-center gap-2 transition-colors">
                                <Pencil className="w-4 h-4" /> Edit Name
                            </button>
                            <button onClick={() => share(active.id)} data-testid="storage-dialog-share"
                                className="h-10 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 font-semibold flex items-center justify-center gap-2 transition-colors">
                                <Share2 className="w-4 h-4" /> Share Link
                            </button>
                            <button onClick={() => remove(active.id)} data-testid="storage-dialog-delete"
                                className="h-10 rounded-sm border border-alert/40 text-alert hover:bg-alert/10 font-semibold flex items-center justify-center gap-2 transition-colors">
                                <Trash2 className="w-4 h-4" /> Delete
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Rename modal */}
            <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
                <DialogContent className="bg-surface border-border max-w-md" data-testid="storage-rename-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Edit clip name</DialogTitle>
                        <DialogDescription>Give this clip a memorable name.</DialogDescription>
                    </DialogHeader>
                    <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} data-testid="storage-rename-input"
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(); }}
                        placeholder="Clip name" className="bg-obsidian border-border rounded-sm h-11" autoFocus />
                    <DialogFooter>
                        <button onClick={() => setRenaming(null)} className="h-10 px-4 rounded-sm border border-border text-muted-foreground hover:text-white">Cancel</button>
                        <button onClick={saveRename} disabled={saving} data-testid="storage-rename-save"
                            className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] disabled:opacity-60">
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
